import { motion, AnimatePresence } from "framer-motion";
import { useMemo, useState } from "react";
import { Sparkles, X } from "lucide-react";
import {
  EXPLORA_DESTINATIONS,
  MAP_VIEWBOX,
  projectToMap,
  type ExploraCategory,
  type ExploraDestination,
} from "@/lib/explora-destinations";
import { MEXICO_MAP_PATH } from "@/assets/mexico-map-path";
import { MEXICO_STATES } from "@/assets/mexico-states";
import { StarField } from "@/components/maya/ui/StarField";

interface MexicoMapProps {
  visibleSlugs: Set<string>;
  highlightedSlugs?: Set<string>;
  selectedSlug?: string | null;
  onSelect: (dest: ExploraDestination) => void;
  countBySlug: Record<string, number>;
  categoryLabel: (c: ExploraCategory) => string;
}

function normState(s: string): string {
  const n = s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  if (n === "cdmx" || n === "ciudad-de-mexico" || n === "distrito-federal")
    return "distrito-federal";
  if (n === "estado-de-mexico" || n === "mexico") return "mexico";
  if (n.startsWith("veracruz")) return "veracruz-de-ignacio-de-la-llave";
  if (n.startsWith("michoacan")) return "michoacan-de-ocampo";
  if (n.startsWith("coahuila")) return "coahuila-de-zaragoza";
  return n;
}

/**
 * "Explora México" — holographic, glass-and-gold interactive map.
 * No political coloring. Mexico floats over a cosmic starfield as a
 * single luminous form; destinations are pulsing nodes you can zoom into.
 */
export function MexicoMap({
  visibleSlugs,
  highlightedSlugs,
  selectedSlug,
  onSelect,
  countBySlug,
  categoryLabel,
}: MexicoMapProps) {
  const [hovered, setHovered] = useState<ExploraDestination | null>(null);

  const selected = useMemo(
    () => EXPLORA_DESTINATIONS.find((d) => d.slug === selectedSlug) ?? null,
    [selectedSlug],
  );

  // ── Zoom-into-destination math ───────────────────────────────────
  // Animate a transform on the map group when a destination is picked.
  const zoom = useMemo(() => {
    if (!selected) return { scale: 1, x: 0, y: 0, originX: 0, originY: 0 };
    const p = projectToMap(selected.lng, selected.lat);
    const scale = 2.4;
    // translate so (p.x, p.y) lands at viewbox center after scaling.
    const x = MAP_VIEWBOX.width / 2 - p.x * scale;
    const y = MAP_VIEWBOX.height / 2 - p.y * scale;
    return { scale, x, y, originX: p.x, originY: p.y };
  }, [selected]);

  const hoveredState = useMemo(() => {
    if (!hovered) return null;
    return normState(hovered.state);
  }, [hovered]);

  const selectedStateKey = useMemo(() => {
    if (!selected) return null;
    return normState(selected.state);
  }, [selected]);

  return (
    <div className="relative w-full overflow-hidden rounded-3xl border border-amber-300/15 bg-[#04060a] shadow-[0_30px_80px_-30px_rgba(0,0,0,0.9)]">
      {/* Living cosmic background */}
      <div className="absolute inset-0 z-0">
        <StarField />
      </div>

      {/* Warm horizon glow at the bottom */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 z-0 h-1/3"
        style={{
          background:
            "radial-gradient(ellipse at 50% 100%, rgba(255,160,70,0.20) 0%, transparent 65%)",
        }}
      />

      {/* HUD chip */}
      <div className="pointer-events-none absolute left-4 top-4 z-20 flex items-center gap-2 rounded-full border border-amber-200/20 bg-black/40 px-3 py-1.5 text-[10px] font-medium uppercase tracking-[0.22em] text-amber-100/80 backdrop-blur-md">
        <span className="h-1.5 w-1.5 rounded-full bg-amber-300 shadow-[0_0_8px_2px_rgba(255,200,90,0.7)]" />
        México · holograma vivo
      </div>
      <div className="pointer-events-none absolute right-4 top-4 z-20 rounded-full border border-amber-200/20 bg-black/40 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-amber-100/80 backdrop-blur-md">
        {EXPLORA_DESTINATIONS.length} destinos
      </div>

      {/* Reset zoom */}
      <AnimatePresence>
        {selected && (
          <motion.button
            type="button"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            onClick={() => onSelect(null as unknown as ExploraDestination)}
            className="absolute right-4 top-14 z-20 flex items-center gap-1.5 rounded-full border border-amber-200/30 bg-black/55 px-3 py-1.5 text-[11px] font-semibold text-amber-100 backdrop-blur-md hover:bg-black/75"
          >
            <X className="h-3.5 w-3.5" /> Salir del zoom
          </motion.button>
        )}
      </AnimatePresence>

      <svg
        viewBox={`0 0 ${MAP_VIEWBOX.width} ${MAP_VIEWBOX.height}`}
        className="relative z-10 block h-auto w-full"
        role="img"
        aria-label="Mapa holográfico interactivo de México"
      >
        <defs>
          {/* Glass fill — warm amber to cool teal across the country */}
          <linearGradient id="mx-glass" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgba(255,180,90,0.14)" />
            <stop offset="55%" stopColor="rgba(255,210,150,0.06)" />
            <stop offset="100%" stopColor="rgba(120,210,220,0.10)" />
          </linearGradient>

          {/* Gold inner stroke */}
          <linearGradient id="mx-stroke" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgba(255,210,140,0.95)" />
            <stop offset="100%" stopColor="rgba(255,140,70,0.95)" />
          </linearGradient>

          {/* Outline glow filter — the "hologram" rim light */}
          <filter id="mx-glow" x="-10%" y="-10%" width="120%" height="120%">
            <feGaussianBlur stdDeviation="2.6" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/* Pin glow */}
          <radialGradient id="pin-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgba(255,210,140,0.95)" />
            <stop offset="60%" stopColor="rgba(255,160,70,0.25)" />
            <stop offset="100%" stopColor="rgba(255,160,70,0)" />
          </radialGradient>

          {/* Country mask: lets us clip the interior dot grid */}
          <clipPath id="mx-clip">
            <path d={MEXICO_MAP_PATH} />
          </clipPath>

          {/* Holographic interior grid (latitude/longitude feel) */}
          <pattern id="mx-grid" width="22" height="22" patternUnits="userSpaceOnUse">
            <path
              d="M 22 0 L 0 0 0 22"
              fill="none"
              stroke="rgba(255,210,140,0.10)"
              strokeWidth="0.4"
            />
          </pattern>
        </defs>

        {/* Animated zoom group */}
        <motion.g
          initial={false}
          animate={{
            scale: zoom.scale,
            x: zoom.x,
            y: zoom.y,
          }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          style={{ transformOrigin: "0 0" }}
        >
          {/* Subtle drop relief — offset blurred silhouette */}
          <g transform="translate(0,6)" opacity="0.35" filter="url(#mx-glow)" pointerEvents="none">
            <path d={MEXICO_MAP_PATH} fill="rgba(255,140,60,0.18)" />
          </g>

          {/* Glass body */}
          <path d={MEXICO_MAP_PATH} fill="url(#mx-glass)" pointerEvents="none" />

          {/* Holographic interior grid, clipped to the country */}
          <g clipPath="url(#mx-clip)" pointerEvents="none">
            <rect
              x="0"
              y="0"
              width={MAP_VIEWBOX.width}
              height={MAP_VIEWBOX.height}
              fill="url(#mx-grid)"
            />
          </g>

          {/* Internal state hairlines — barely there, just enough relief */}
          <g opacity="0.55">
            {MEXICO_STATES.map((s) => {
              const isSelectedState = s.slug === selectedStateKey;
              const isHoveredState = s.slug === hoveredState;
              const highlight = isSelectedState || isHoveredState;
              return (
                <path
                  key={s.slug}
                  d={s.d}
                  fill={
                    highlight
                      ? "rgba(255,180,90,0.10)"
                      : "transparent"
                  }
                  stroke={
                    highlight
                      ? "rgba(255,210,140,0.55)"
                      : "rgba(255,210,140,0.10)"
                  }
                  strokeWidth={highlight ? 0.55 : 0.3}
                  style={{
                    transition: "stroke 250ms ease, fill 250ms ease",
                    pointerEvents: "none",
                  }}
                />
              );
            })}
          </g>

          {/* Outline — glow then crisp line */}
          <path
            d={MEXICO_MAP_PATH}
            fill="none"
            stroke="rgba(255,170,80,0.55)"
            strokeWidth="2.4"
            filter="url(#mx-glow)"
            pointerEvents="none"
          />
          <path
            d={MEXICO_MAP_PATH}
            fill="none"
            stroke="url(#mx-stroke)"
            strokeWidth="0.9"
            strokeLinejoin="round"
            pointerEvents="none"
          />

          {/* Destination nodes */}
          {EXPLORA_DESTINATIONS.map((d) => {
            const { x, y } = projectToMap(d.lng, d.lat);
            const isVisible = visibleSlugs.has(d.slug);
            const isHighlighted = highlightedSlugs?.has(d.slug);
            const isSelected = selected?.slug === d.slug;
            const isHovered = hovered?.slug === d.slug;
            const count = countBySlug[d.slug] ?? 0;
            const hasTours = count > 0;
            const active = isHovered || isSelected || isHighlighted;
            // Counter-scale the pin so it stays visually consistent when zoomed.
            const counterScale = isSelected ? 1 / zoom.scale : 1;

            return (
              <g
                key={d.slug}
                transform={`translate(${x},${y}) scale(${counterScale})`}
                style={{
                  cursor: isVisible ? "pointer" : "default",
                  opacity: isVisible ? 1 : 0.35,
                  transition: "opacity 350ms ease",
                }}
                onClick={() => isVisible && onSelect(d)}
                onMouseEnter={() => isVisible && setHovered(d)}
                onMouseLeave={() =>
                  setHovered((h) => (h?.slug === d.slug ? null : h))
                }
              >
                {/* Soft halo */}
                <circle r={active ? 22 : 16} fill="url(#pin-glow)" />

                {/* Pulse ripples — always on for tour-bearing destinations */}
                {hasTours && (
                  <>
                    <motion.circle
                      r={4}
                      fill="none"
                      stroke="rgba(255,210,140,0.9)"
                      strokeWidth={0.6}
                      initial={false}
                      animate={{ r: [4, 14, 4], opacity: [0.9, 0, 0.9] }}
                      transition={{
                        duration: 2.6,
                        repeat: Infinity,
                        ease: "easeOut",
                      }}
                    />
                    <motion.circle
                      r={4}
                      fill="none"
                      stroke="rgba(255,210,140,0.6)"
                      strokeWidth={0.4}
                      initial={false}
                      animate={{ r: [4, 22, 4], opacity: [0.6, 0, 0.6] }}
                      transition={{
                        duration: 3.4,
                        repeat: Infinity,
                        ease: "easeOut",
                        delay: 0.4,
                      }}
                    />
                  </>
                )}

                {/* AI suggestion ring */}
                {isHighlighted && (
                  <motion.circle
                    r={9}
                    fill="none"
                    stroke="rgba(255,255,255,0.9)"
                    strokeWidth={0.9}
                    strokeDasharray="2 3"
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 18,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                  />
                )}

                {/* Core pin: gold disc with crisp rim */}
                <circle
                  r={active ? 5.5 : 4}
                  fill={
                    hasTours
                      ? "rgba(255,200,110,1)"
                      : "rgba(255,255,255,0.85)"
                  }
                  stroke="rgba(255,255,255,0.95)"
                  strokeWidth={1.1}
                />
                <circle r={active ? 2.2 : 1.6} fill="#fff" opacity={0.95} />

                {/* Label when active */}
                {(active || isHighlighted) && (
                  <text
                    y={-12}
                    textAnchor="middle"
                    style={{
                      font: `600 ${active ? 11 : 10}px var(--font-display, ui-sans-serif)`,
                      fill: "rgba(255,235,200,1)",
                      paintOrder: "stroke",
                      stroke: "rgba(0,0,0,0.85)",
                      strokeWidth: 2.6,
                      strokeLinejoin: "round",
                      pointerEvents: "none",
                    }}
                  >
                    {d.name}
                  </text>
                )}
              </g>
            );
          })}
        </motion.g>
      </svg>

      {/* Maya whisper — appears on hover */}
      <AnimatePresence>
        {hovered && (
          <motion.div
            key={`whisper-${hovered.slug}`}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.2 }}
            className="pointer-events-none absolute bottom-4 left-1/2 z-30 -translate-x-1/2 rounded-full border border-amber-200/25 bg-black/60 px-4 py-2 text-xs text-amber-100/95 shadow-[0_8px_30px_-6px_rgba(0,0,0,0.6)] backdrop-blur-md"
          >
            <span className="mr-2 inline-flex h-1.5 w-1.5 -translate-y-px rounded-full bg-amber-300 align-middle shadow-[0_0_8px_2px_rgba(255,200,90,0.7)]" />
            <span className="font-semibold text-amber-200">Maya:</span>{" "}
            Encontré{" "}
            <span className="font-semibold text-amber-200">
              {countBySlug[hovered.slug] ?? 0}
            </span>{" "}
            experiencia{(countBySlug[hovered.slug] ?? 0) === 1 ? "" : "s"} en{" "}
            <span className="font-semibold text-amber-100">{hovered.name}</span>.
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hover tooltip card — anchored to the cursor target */}
      <AnimatePresence>
        {hovered && !selected && (
          <motion.div
            key={`tip-${hovered.slug}`}
            initial={{ opacity: 0, y: 4, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.96 }}
            transition={{ duration: 0.18 }}
            className="pointer-events-none absolute z-30 w-[min(240px,55vw)] -translate-x-1/2 -translate-y-full rounded-2xl border border-amber-200/20 bg-black/70 p-3 text-xs text-amber-50 shadow-[0_18px_40px_-12px_rgba(0,0,0,0.8)] backdrop-blur-md"
            style={{
              left: `${(projectToMap(hovered.lng, hovered.lat).x / MAP_VIEWBOX.width) * 100}%`,
              top: `calc(${(projectToMap(hovered.lng, hovered.lat).y / MAP_VIEWBOX.height) * 100}% - 22px)`,
            }}
          >
            <div className="text-[10px] uppercase tracking-[0.18em] text-amber-200/70">
              {hovered.state}
            </div>
            <div className="mt-0.5 font-display text-sm font-semibold text-amber-100">
              {hovered.name}
            </div>
            <div className="mt-1 text-amber-50/75">
              <span className="font-semibold text-amber-200">
                {countBySlug[hovered.slug] ?? 0}
              </span>{" "}
              experiencias disponibles
            </div>
            <div className="mt-1.5 flex flex-wrap gap-1">
              {hovered.categories.slice(0, 3).map((c) => (
                <span
                  key={c}
                  className="rounded-full border border-amber-200/20 bg-amber-300/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-200"
                >
                  {categoryLabel(c)}
                </span>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* AI suggestion badge */}
      <AnimatePresence>
        {highlightedSlugs && highlightedSlugs.size > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="absolute bottom-4 right-4 z-30 flex items-center gap-1.5 rounded-full bg-gradient-to-r from-amber-300 to-orange-400 px-3 py-1.5 text-[11px] font-semibold text-[#1a0a02] shadow-[0_8px_24px_-6px_rgba(255,160,70,0.55)]"
          >
            <Sparkles className="h-3 w-3" />
            {highlightedSlugs.size} sugerido
            {highlightedSlugs.size === 1 ? "" : "s"} por Maya
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
